import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import ConnexisGlobe from "./ConnexisGlobe";
import StarField from "./StarField";

/**
 * AboutHero v6 — final polish.
 *
 *   ┌────────────────────────────────────────────────┐
 *   │  TEXT ZONE (clean navy with stars)              │
 *   │     [icon with blue halo]                       │
 *   │            Connexis.                            │
 *   │   AI INFRASTRUCTURE · LEAD GEN · FIN SERVICES   │
 *   │              ────                                │
 *   │        The connective tissue of                 │
 *   │            modern finance.                      │
 *   │      "Connecting consumer demand..."            │
 *   │       [Buy leads]   [Book strategy call]        │
 *   ├────────────────────────────────────────────────┤
 *   │  GLOBE ZONE (full circular globe, no overlap)   │
 *   │            ╭─────────╮                          │
 *   │         /              \                        │
 *   │        |   FULL EARTH   |                       │
 *   │         \              /                        │
 *   │            ╰─────────╯                          │
 *   └────────────────────────────────────────────────┘
 *
 *  - Starfield covers the WHOLE section (top + bottom)
 *  - No fade-to-white (the next section is also navy)
 *  - Continent dots are LIGHT BLUE, not white
 */
export default function AboutHero() {
  return (
    <section className="relative w-full bg-[#0F172A] text-white overflow-hidden" data-testid="about-hero">
      <style>{`
        @keyframes connexisHaloPulse {
          0%, 100% {
            box-shadow:
              0 0 30px rgba(61,123,255,0.55),
              0 0 90px rgba(61,123,255,0.35),
              0 0 180px rgba(61,123,255,0.18);
            transform: scale(1);
          }
          50% {
            box-shadow:
              0 0 50px rgba(61,123,255,0.95),
              0 0 140px rgba(61,123,255,0.55),
              0 0 280px rgba(61,123,255,0.28);
            transform: scale(1.06);
          }
        }
        .connexis-halo { animation: connexisHaloPulse 2.8s ease-in-out infinite; }
        @keyframes liveDotFlicker {
          0%, 100% { opacity: 1;    box-shadow: 0 0 8px rgba(61,123,255,0.85); }
          50%      { opacity: 0.35; box-shadow: 0 0 4px rgba(61,123,255,0.35); }
        }
        .live-dot { animation: liveDotFlicker 1.6s ease-in-out infinite; }
      `}</style>

      {/* STARFIELD — shared, continuous across all dark sections */}
      <StarField />

      {/* Ambient deep-space radial gradients — much softer now so they don't wash out stars */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(20,40,90,0.35),_transparent_60%)] pointer-events-none z-0" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center_75%,_rgba(20,60,180,0.18),_transparent_55%)] pointer-events-none z-0" />

      {/* ─── ZONE 1: text content ─── */}
      <div className="relative z-10 mx-auto max-w-5xl px-6 pt-16 sm:pt-20 lg:pt-24 pb-12 text-center">
        {/* 1. BRAND ICON with strong blue halo */}
        <div className="flex justify-center" data-testid="about-hero-brand-icon">
          <span className="connexis-halo inline-flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-2xl bg-[#0a1124] ring-1 ring-white/15">
            <svg viewBox="0 0 24 24" className="h-7 w-7 sm:h-8 sm:w-8" fill="none" stroke="currentColor" strokeWidth="2.1">
              <path d="M5 7h7M5 12h12M5 17h7" strokeLinecap="round" stroke="#ffffff" />
              <circle cx="18" cy="7" r="2.4" fill="#3d7bff" stroke="none" />
              <circle cx="18" cy="17" r="2.4" fill="#3d7bff" stroke="none" />
            </svg>
          </span>
        </div>

        {/* 2. "Connexis." wordmark — white with blue dot */}
        <div className="mt-7 sm:mt-9 text-[44px] sm:text-[56px] lg:text-[64px] font-semibold tracking-[-0.035em] leading-none text-white" data-testid="about-hero-wordmark">
          Connexis<span className="text-[#3d7bff]">.</span>
        </div>

        {/* 3. CAPS pills with blue separator dots */}
        <div className="mt-5 inline-flex items-center gap-3 sm:gap-5 text-[10.5px] sm:text-[11.5px] tracking-[0.28em] uppercase text-white/65 font-medium" data-testid="about-hero-tagline">
          <span>AI Infrastructure</span>
          <span className="live-dot inline-block size-1 rounded-full bg-[#3d7bff]" style={{ animationDelay: "0s" }} />
          <span>Lead Generation</span>
          <span className="live-dot inline-block size-1 rounded-full bg-[#3d7bff]" style={{ animationDelay: "0.5s" }} />
          <span>Financial Services</span>
        </div>

        {/* 4. Tiny divider line */}
        <div className="mt-5 mx-auto h-px w-12 bg-gradient-to-r from-transparent via-[#3d7bff]/65 to-transparent" />

        {/* 5. H1 STATEMENT */}
        <h1 className="mt-12 sm:mt-14 text-[40px] sm:text-[56px] lg:text-[64px] tracking-[-0.045em] font-semibold leading-[1.02]">
          The connective tissue of<br />
          <span className="bg-gradient-to-r from-white via-[#9cc4ff] to-[#3d7bff] bg-clip-text text-transparent">
            modern finance.
          </span>
        </h1>

        {/* 6. BODY */}
        <p className="mt-5 max-w-xl mx-auto text-[15.5px] sm:text-[16px] text-white/60 leading-relaxed">
          Connecting consumer demand, AI workflows and the financial brands that turn enquiries into revenue — through one intelligent network.
        </p>

        {/* 7. CTAS */}
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link to="/buy-leads">
            <button data-testid="about-hero-cta-primary" className="inline-flex items-center gap-1.5 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white px-5 h-11 text-[14.5px] font-semibold shadow-[0_8px_28px_rgba(0,85,255,0.45)] transition-all hover:-translate-y-0.5">
              Buy exclusive leads <ArrowUpRight className="size-4" />
            </button>
          </Link>
          <Link to="/book-demo">
            <button data-testid="about-hero-cta-secondary" className="inline-flex items-center gap-1.5 rounded-full border border-white/20 hover:border-white/45 hover:bg-white/[0.05] text-white px-5 h-11 text-[14.5px] font-medium transition-colors backdrop-blur-sm">
              Book a strategy call <ArrowRight className="size-4" />
            </button>
          </Link>
        </div>
      </div>

      {/* ─── ZONE 2: full globe — its own clean zone, no text overlap ─── */}
      <div
        className="relative z-0 w-full h-[440px] sm:h-[520px] lg:h-[600px]"
        data-testid="about-hero-globe-frame"
      >
        <ConnexisGlobe />
      </div>
    </section>
  );
}
